import { useEffect, useState } from "react";
import { Calendar, Coins, Brain, BarChart3, Bot, BookOpen, FileText, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import { api } from "../../lib/api";
import LearningStyleBadge from "../../components/LearningStyleBadge";
import CoinBadge from "../../components/CoinBadge";

export default function Dashboard() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/dashboards/me").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return <div className="p-6 text-edu-muted">Cargando…</div>;
    const { user, stats, recent_lessons, recent_exams, transactions, charts } = data;
    const today = new Date().toLocaleDateString("es-PE", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="alumno-dashboard">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A]">
                        ¡Bienvenido/a {user.name}! <span aria-hidden>👋</span>
                    </h1>
                    <p className="text-edu-body mt-1">Aquí está tu resumen de aprendizaje de hoy.</p>
                </div>
                <div className="inline-flex items-center gap-2 text-sm text-edu-body bg-white border border-edu rounded-full px-4 py-2">
                    <Calendar size={14} className="text-edu-navy" />
                    <span className="capitalize">{today}</span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                <div
                    className="bg-edu-gold rounded-2xl p-6 text-[#0A0A0A] shadow-[0_8px_30px_rgb(245,166,35,0.25)]"
                    data-testid="card-coins"
                >
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest opacity-70">
                                Saldo de Monedas
                            </p>
                            <h2 className="text-5xl font-bold mt-2">{stats.coin_balance}</h2>
                            <p className="text-xs mt-3 leading-relaxed max-w-[220px] opacity-80">
                                Disponibles para usar en lecciones, exámenes y tutor IA.
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-black/10 flex items-center justify-center">
                            <Coins size={22} />
                        </div>
                    </div>
                </div>

                <div className="edu-card p-6" data-testid="card-style">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-edu-muted">
                                Estilo de Aprendizaje
                            </p>
                            <h2 className="text-2xl font-semibold mt-2 capitalize text-edu-navy">
                                {stats.learning_style || "Sin asignar"}
                            </h2>
                            <p className="text-xs mt-3 leading-relaxed max-w-[220px] text-edu-body">
                                Tu contenido está adaptado a tu forma única de aprender.
                            </p>
                            <div className="mt-3">
                                <LearningStyleBadge style={stats.learning_style} />
                            </div>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-edu-navy/10 flex items-center justify-center">
                            <Brain size={22} className="text-edu-navy" />
                        </div>
                    </div>
                </div>

                <div className="edu-card p-6" data-testid="card-activity">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <p className="text-xs font-bold uppercase tracking-widest text-edu-muted">
                                Actividad Reciente
                            </p>
                            <div className="grid grid-cols-3 gap-3 mt-4">
                                <div>
                                    <div className="text-3xl font-bold text-edu-navy">{stats.lessons_week}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-edu-muted">
                                        Lecciones / sem.
                                    </div>
                                </div>
                                <div>
                                    <div className="text-3xl font-bold text-edu-navy">{stats.exams_week}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-edu-muted">
                                        Exámenes / sem.
                                    </div>
                                </div>
                                <div>
                                    <div className="text-3xl font-bold text-edu-navy">
                                        {stats.ai_sessions_week}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-edu-muted">
                                        Sesiones IA
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-edu-navy/10 flex items-center justify-center">
                            <BarChart3 size={22} className="text-edu-navy" />
                        </div>
                    </div>
                </div>
            </div>

            <h2 className="text-xl font-semibold mb-4">Acciones Rápidas</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                <QuickCard
                    Icon={Bot}
                    title="Tutor IA"
                    desc="Resuelve dudas con tu tutor personalizado."
                    to="/alumno/tutor-ia"
                    label="Iniciar Chat"
                    testId="quick-tutor"
                />
                <QuickCard
                    Icon={BookOpen}
                    title="Lecciones"
                    desc="Explora la biblioteca adaptada a tu estilo."
                    to="/alumno/lecciones"
                    label="Ver Lecciones"
                    testId="quick-lessons"
                />
                <QuickCard
                    Icon={FileText}
                    title="Exámenes"
                    desc="Pon a prueba lo que has aprendido."
                    to="/alumno/examenes"
                    label="Tomar Examen"
                    testId="quick-exams"
                />
            </div>

            <h2 className="text-xl font-semibold mb-4 inline-flex items-center gap-2">
                <TrendingUp size={20} className="text-edu-navy" /> Tu progreso
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10" data-testid="alumno-charts">
                <div className="edu-card p-5" data-testid="chart-weekly-activity">
                    <h3 className="text-sm font-semibold text-edu-navy mb-3">Actividad semanal</h3>
                    {charts?.weekly_activity?.length ? (
                        <ResponsiveContainer width="100%" height={210}>
                            <BarChart data={charts.weekly_activity}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar dataKey="lecciones" fill="#1B2F6E" radius={[5, 5, 0, 0]} />
                                <Bar dataKey="examenes" fill="#F5A623" radius={[5, 5, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="text-xs text-edu-muted">Sin datos aún.</p>
                    )}
                </div>
                <div className="edu-card p-5" data-testid="chart-exam-progress">
                    <h3 className="text-sm font-semibold text-edu-navy mb-3">Evolución de notas</h3>
                    {charts?.exam_progress?.length ? (
                        <ResponsiveContainer width="100%" height={210}>
                            <LineChart data={charts.exam_progress}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                <XAxis dataKey="fecha" tick={{ fontSize: 10 }} />
                                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Line
                                    type="monotone"
                                    dataKey="puntaje"
                                    stroke="#1B2F6E"
                                    strokeWidth={3}
                                    dot={{ fill: "#F5A623", r: 4 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="text-xs text-edu-muted">Aún no has rendido exámenes.</p>
                    )}
                </div>
                <div className="edu-card p-5" data-testid="chart-by-subject">
                    <h3 className="text-sm font-semibold text-edu-navy mb-3">Promedio por materia</h3>
                    {charts?.by_subject?.length ? (
                        <ResponsiveContainer width="100%" height={210}>
                            <RadarChart data={charts.by_subject}>
                                <PolarGrid stroke="#E5E7EB" />
                                <PolarAngleAxis dataKey="materia" tick={{ fontSize: 11 }} />
                                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9 }} />
                                <Radar dataKey="promedio" stroke="#1B2F6E" fill="#1B2F6E" fillOpacity={0.4} />
                                <Tooltip />
                            </RadarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="text-xs text-edu-muted">Aún no hay calificaciones por materia.</p>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="edu-card p-6">
                    <h3 className="text-lg font-semibold mb-4">Actividad Reciente</h3>
                    <div className="space-y-3">
                        {recent_lessons.length === 0 && recent_exams.length === 0 && (
                            <p className="text-sm text-edu-muted">Aún no tienes actividad. ¡Empieza una lección!</p>
                        )}
                        {recent_lessons.map((l, i) => (
                            <div
                                key={`l-${i}`}
                                className="flex items-center justify-between p-3 rounded-xl bg-[#F8F9FB]"
                            >
                                <div>
                                    <div className="font-medium">{l.title}</div>
                                    <div className="text-xs text-edu-muted">{l.subject}</div>
                                </div>
                                <div className="text-xs text-edu-muted">{(l.date || "").slice(0, 10)}</div>
                            </div>
                        ))}
                        {recent_exams.map((e, i) => (
                            <div
                                key={`e-${i}`}
                                className="flex items-center justify-between p-3 rounded-xl bg-[#F8F9FB]"
                            >
                                <div>
                                    <div className="font-medium">Examen: {e.exam_title}</div>
                                    <div className="text-xs text-edu-muted">
                                        {e.subject} · {e.score_percent}%
                                    </div>
                                </div>
                                <div className="text-xs text-edu-muted">
                                    {(e.submitted_at || "").slice(0, 10)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="edu-card p-6">
                    <h3 className="text-lg font-semibold mb-4">Historial de Monedas</h3>
                    <div className="space-y-2">
                        {transactions.length === 0 && (
                            <p className="text-sm text-edu-muted">Sin transacciones aún.</p>
                        )}
                        {transactions.map((t, i) => (
                            <div
                                key={i}
                                className="flex items-center justify-between p-3 rounded-xl bg-[#F8F9FB]"
                            >
                                <div>
                                    <div className="text-sm font-medium">{t.concept}</div>
                                    <div className="text-xs text-edu-muted">
                                        {(t.created_at || "").slice(0, 10)}
                                    </div>
                                </div>
                                <CoinBadge amount={t.amount} size="sm" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function QuickCard({ Icon, title, desc, to, label, testId }) {
    return (
        <div className="edu-card p-6 flex flex-col" data-testid={testId}>
            <div className="w-12 h-12 rounded-xl bg-edu-navy/5 flex items-center justify-center mb-4">
                <Icon size={22} className="text-edu-navy" />
            </div>
            <h4 className="text-lg font-semibold">{title}</h4>
            <p className="text-sm text-edu-body mb-4 flex-1">{desc}</p>
            <Link
                to={to}
                className="edu-btn-primary inline-flex items-center justify-center px-4 py-2 text-sm font-medium"
                data-testid={`${testId}-cta`}
            >
                {label}
            </Link>
        </div>
    );
}
